<template>
  <div>
    <div class="container-fluid chart bubble-chart-emission">
      <wj-flex-chart
        chartType="Bubble"
        :itemsSource="dataChart"
        :itemFormatter="customFormatter"
        :palette="palette"
        :initialized="onChartInitialized"
      >
        <wj-flex-chart-axis wjProperty="axisY" :title="axisYTitle" :min="0" :axisLine="true"></wj-flex-chart-axis>
        <wj-flex-chart-series :binding="`${yAxisKey},${bubbleSizeKey}`"></wj-flex-chart-series>
      </wj-flex-chart>
    </div>
    <div id="customTooltip" ref="tooltipRef"></div>
  </div>
</template>
<script>
import '@mescius/wijmo.styles/wijmo.css';
import '@mescius/wijmo.vue2.chart';
import { isDashboardProductCFP } from '@/api/product/emissionsProduct';
import { mapActions, mapState } from 'vuex';
import { prepareChartAxisYLineChart, formatYAxisSmallerThan1 } from '@/concerns/chart';
import { formatBigNumber } from '@/concerns/newRegisterData/wijmo.helper';
import { addThoundSandCommaWithTwoDigit, formatNumberByConditions } from '@/utils/convertNumber';
import { UNIT_SETTING } from '@/constants/dashboard';
import { numberFormat } from '@/filters/number';
import { getUnitNewDashboardCFP } from '@/utils/new-cfp-dashboard';
import { GHG_PER_PRODUCT } from '@/constants/ghg-per-product';

export default {
  props: {
    filterType: {
      type: Number,
      default: 1,
    },
    axisConfigData: {
      type: Object,
    },
    colorConfig: {
      type: Object,
    },
    dataChartProp: {
      type: Array,
    },
    xAxisKey: {
      type: String,
    },
    yAxisKey: {
      type: String,
    },
    bubbleSizeKey: {
      type: String,
    },
    productsByCategory: {
      type: Array,
    },
  },
  data() {
    return {
      selectDataList: [
        { value: 1, text: this.$t('product_dashboard_main.pulldown_select_production_quantity') },
        { value: 2, text: this.$t('product_dashboard_main.pulldown_select_production_value') },
      ],
      unitDataList: [],
      data: [],
      palette: ['#A9A04B'],
      chart: null,
      dataChart: [],
      easing: 'EaseInQuad',
      animationMode: 'Point',
      axisYTitle: '',
      colorMapping: {
        bgRed: '#E45B58',
        bgBlue: '#008FCC',
        bgGreen: '#80BC62',
        bgOrange: '#E0A329',
        bgPurple: '#9F62BD',
      },
      mappingTitle: {
        [GHG_PER_PRODUCT.PRODUCT_QUANTITY]: this.$t('bubble_chart.option_product_quantity_scatter_plot_setting'),
        [GHG_PER_PRODUCT.PRODUCT_EMISSION]: this.$t('bubble_chart.option_discharge_volume_scatter_plot_setting'),
        [GHG_PER_PRODUCT.DECLARATION_UNIT_EMISSION]: this.$t(
          'bubble_chart.option_unit_discharge_amount_scatter_plot_setting',
        ),
        [GHG_PER_PRODUCT.ANNUAL_PRODUCT]: this.$t('bubble_chart.option_annual_production_volume_scatter_plot_setting'),
        [GHG_PER_PRODUCT.ANNUAL_PRODUCT_EMISSION]: this.$t('bubble_chart.option_annual_output_scatter_plot_setting'),
        [GHG_PER_PRODUCT.PRODUCT_PRICE]: this.$t('bubble_chart.option_product_amount'),
        [GHG_PER_PRODUCT.ANNUAL_REVENUE]: this.$t('bubble_chart.option_years_of_sale_scatter_plot_setting'),
      },
      isMouseOverTooltip: false,
      defaultColor: '#A9A04B',
      bubbleDefaultColor: '#948810cc',
      bubbleNegativeDefaultColor: '#f5f5eb',
    };
  },
  computed: {
    isNewDashboarRoute() {
      return isDashboardProductCFP(this.$route);
    },
    isDisabled() {
      return this.selectDataList.length === 1;
    },
    dropdownIcon() {
      return this.selectDataList.length > 1 ? '$dropdown' : '';
    },
    getUnitChart() {
      return getUnitNewDashboardCFP();
    },
    selectedYearValue() {
      return this.selectDataList[0]?.text;
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    if (isDashboardProductCFP(this.$route)) {
      this.selectDataList.pop();
    }

    const isTouchDevice = () => matchMedia('(pointer: coarse)').matches;

    if (!isTouchDevice()) {
      this.$refs.tooltipRef.addEventListener('mouseenter', () => (this.isMouseOverTooltip = true));
      this.$refs.tooltipRef.addEventListener('mouseleave', () => {
        this.isMouseOverTooltip = false;
        this.$refs.tooltipRef.style.opacity = 0;
        this.$refs.tooltipRef.style.zIndex = -1;
      });
    }

    document.addEventListener('click', (event) => {
      const tooltip = this.$refs.tooltipRef;
      const chart = this.chart;

      if (!tooltip || !chart) return;

      const isClickInsideTooltip = tooltip.contains(event.target);
      const isClickInsideChart = chart.hostElement.contains(event.target);

      if (!isClickInsideTooltip && !isClickInsideChart) {
        tooltip.style.opacity = 0;
        tooltip.style.zIndex = -1;
        this.isMouseOverTooltip = false;
      }
    });
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    ...mapActions('product', ['updateNewEmissionsPerProducts']),
    customFormatter(engine, ht, defaultRenderer) {
      if (ht.chartElement && ht.item) {
        const ctx = engine;

        // Tính toán tỷ lệ kích thước bubble
        const sizeValue = ht.item[this.bubbleSizeKey];
        const allSizes = this.dataChart.map((item) => item[this.bubbleSizeKey]);
        const maxSize = Math.max(...allSizes);
        const sizeRatio = (sizeValue / maxSize) || 0;

        const maxRadius = 44; // ~ bubble maxSize = 88
        const minRadius = 2.5; // ~ bubble minSize = 5

        const targetArea = Math.PI * Math.pow(maxRadius, 2) * sizeRatio;
        let radius = Math.sqrt(targetArea / Math.PI);

        if (radius < minRadius) {
          radius = minRadius;
        }

        // === Logic màu sắc theo yêu cầu ===
        let fillColor, strokeColor;

        if (ht.item.color && ht.item.color !== 'defaultColor') {
          // Có màu riêng từ colorMapping
          if (ht.item.actualBubbleSizeData < 0) {
            fillColor = `${this.colorMapping[ht.item.color]}1a`; // 10% opacity
            strokeColor = `${this.colorMapping[ht.item.color]}1a`;
          } else {
            fillColor = this.colorMapping[ht.item.color];
            strokeColor = this.colorMapping[ht.item.color];
          }
        } else {
          // Màu mặc định
          if (ht.item.actualBubbleSizeData < 0) {
            fillColor = this.bubbleNegativeDefaultColor;
            strokeColor = this.bubbleNegativeDefaultColor;
          } else {
            fillColor = this.bubbleDefaultColor;
            strokeColor = this.bubbleDefaultColor;
          }
        }

        ctx.fill = fillColor;
        ctx.stroke = strokeColor;

        ctx.drawEllipse(ht.point.x, ht.point.y, radius, radius);
        return;
      }

      defaultRenderer();
    },

    onChartInitialized(flexchart) {
      this.chart = flexchart;
      flexchart.bindingX = this.xAxisKey;
      flexchart.legend.position = 0; // remove chart legend
      flexchart.axisX.majorTickMarks = false;
      flexchart.axisY.axisLine = true;
      flexchart.axisY.majorGrid = true;
      flexchart.axisY.labelPadding = 16;
      flexchart.axisX.labelPadding = 16;
      flexchart.tooltip.content = '';
      flexchart.options = {
        bubble: {
          minSize: 5,
          maxSize: 88,
        },
      };

      this.updateAxisYTitle();
      this.updateAxisXTitle();

      const isTouchDevice = () => matchMedia('(pointer: coarse)').matches;
      flexchart.hostElement.addEventListener('mousemove', (event) =>
        this.setupTooltip(event, flexchart, isTouchDevice()),
      );
    },
    handleResize() {
      this.updateAxisYTitle();
    },
    updateAxisYTitle() {
      if (window.innerWidth <= 450) {
        this.axisYTitle = '';
      } else {
        const verticalAxis = this.axisConfigData?.verticalAxis;
        const label = this.mappingTitle[verticalAxis?.topic];
        const unit = verticalAxis?.unit?.name === null || !verticalAxis?.unit ? '---' : verticalAxis?.unit.name;
        this.axisYTitle = `${label} (${unit})`;
      }
      if (this.chart) {
        this.chart.refresh(true);
      }
    },
    updateAxisXTitle() {
      const horizontalAxis = this.axisConfigData?.horizontalAxis;
      const label = this.mappingTitle[horizontalAxis?.topic];
      const unit = horizontalAxis?.unit?.name === null || !horizontalAxis?.unit ? '---' : horizontalAxis?.unit.name;
      this.chart.axisX.title = `${label} (${unit})`;
    },

    parseTinyNumber(str) {
      const val = Number(str);
      if (val === 0) return { value: 0, text: '0' };

      const exponent = Math.abs(Math.floor(Math.log10(Math.abs(val))));
      const fixed = Math.min(exponent + 1, 30);
      const sign = val < 0 ? '-' : '';
      const absVal = Math.abs(val);
      const formatted = absVal.toFixed(fixed).replace(/0+$/, '').replace(/\.$/, ''); // ✅ fix ở đây

      return {
        value: val,
        text: sign + formatted,
      };
    },

    getSmartScaleFactor(min, max) {
      const maxAbs = Math.max(Math.abs(min), Math.abs(max));
      if (maxAbs === 0) return 1; // tránh chia 0
      if (maxAbs < 1e-6) return 1e20 * 10;
      if (maxAbs < 1e-3) return 1e10 * 10;
      return 1; // không scale nếu số đã đủ lớn
    },

    prepareAxis() {
      let calcAxisX = prepareChartAxisYLineChart(
        this.dataChart.map((item) => item[this.xAxisKey]),
        8,
      );
      let calcAxisY = prepareChartAxisYLineChart(
        this.dataChart.map((item) => item[this.yAxisKey]),
        7,
      );

      if (this.dataChart.length === 0) {
        calcAxisY = [0, 175, 25];
        calcAxisX = [0, 800, 100];
      }

      const min = calcAxisY[0];
      const max = calcAxisY[1];
      const unit = calcAxisY[2];
      const scaleFactor = this.getSmartScaleFactor(min, max);

      // Scale dữ liệu cho biểu đồ
      this.chart.axisY.min = min * scaleFactor;
      this.chart.axisY.max = max * scaleFactor;
      this.chart.axisY.majorUnit = unit * scaleFactor;

      // Format hiển thị
      this.chart.axisY.itemFormatter = (engine, label) => {
        const maxY = this.chart.axisY.max;
        const EPSILON = 1e-10;

        // Xác định tick cuối
        const isLastTick = Math.abs(label.val - maxY) < EPSILON;

        if (isLastTick) {
          // Đặt nhãn cuối là đơn vị đo
          label.text = this.axisConfigData?.verticalAxis?.unit?.name || '';
          label.cls += ' unit';

          // Ẩn grid line cuối cùng (nếu cần)
          const gridLines = document.querySelectorAll('.wj-gridline');
          gridLines.forEach((line, index) => {
            if (index === gridLines.length - 1) {
              line.style.display = 'none';
            }
          });
        } else {
          const realValue = label.val / scaleFactor;
          const parsed = this.parseTinyNumber(realValue.toString());
          label.text = formatYAxisSmallerThan1(numberFormat(parsed.text));
        }
        return label;
      };

      this.chart.axisX.majorUnit = calcAxisX[2];
      // set min max value for axisX
      this.chart.axisX.min = calcAxisX[0];
      this.chart.axisX.max = calcAxisX[1];
      this.chart.axisX.itemFormatter = (_, label) => {
        label.text = formatYAxisSmallerThan1(
          numberFormat(label.val.toString()),
          this.calSelected == 1 ? 't-CO2' : 'currency',
        );
        return label;
      };
    },

    findMax(dataChart, key) {
      return dataChart.reduce((max, product) => {
        return Math.max(max, product[key]);
      }, 0);
    },
    findMin(dataChart, key) {
      return dataChart.reduce((min, product) => {
        return Math.min(min, product[key]);
      }, 0);
    },
    formatNumber(num) {
      if (['0', '0.00', 0].includes(num)) {
        return '0.00';
      }
      return addThoundSandCommaWithTwoDigit(formatBigNumber(num, 50).replace(/[^0-9\.\-]+/g, ''), '', false, true);
    },
    formatNumberBySetting(num, option) {
      if (num === null) {
        return '-';
      }
      return formatNumberByConditions(num, option);
    },
    setupTooltip(e, chart, isMobile) {
      const ht = chart.hitTest(e);
      const tooltip = this.$refs.tooltipRef;
      const sidebar = document.getElementsByClassName('v-navigation-drawer__content')[0];
      const toolbar = document.getElementsByClassName('v-toolbar__content')[0];

      if (ht && ht.item) {
        const sizeValue = ht.item[this.bubbleSizeKey];
        const allSizes = this.dataChart.map((item) => item[this.bubbleSizeKey]);
        const maxSize = Math.max(...allSizes);
        const sizeRatio = (sizeValue / maxSize) || 0;

        const maxRadius = 44; // ~ bubble maxSize = 88
        const minRadius = 2.5; // ~ bubble minSize = 5
        const targetArea = Math.PI * Math.pow(maxRadius, 2) * sizeRatio;
        let radius = Math.sqrt(targetArea / Math.PI);
        if (radius < minRadius) radius = minRadius;

        const chartX = chart.axisX.convert(ht.item[this.xAxisKey]);
        const chartY = chart.axisY.convert(ht.item[this.yAxisKey]);

        const rect = chart.hostElement.getBoundingClientRect();
        const mouseX = e.pageX - rect.left - window.scrollX;
        const mouseY = e.pageY - rect.top - window.scrollY;

        const dx = mouseX - chartX;
        const dy = mouseY - chartY;
        const distanceToCenter = Math.sqrt(dx * dx + dy * dy);

        if (distanceToCenter <= radius) {
          tooltip.innerHTML = this.customTooltip(ht);

          const detailLink = tooltip.querySelector('#product-detail-link');
          if (detailLink) {
            detailLink.addEventListener('click', () => {
              this.redirectToDetailProduct(ht.item);
            });
          }

          const offsetX = 0,
                offsetY = 0;

          let left = e.pageX + offsetX;
          let top = e.pageY + offsetY;

          const tooltipWidth = tooltip.clientWidth;
          const tooltipHeight = tooltip.clientHeight;
          const chartRight = rect.left + rect.width + window.scrollX;
          const chartBottom = rect.top + rect.height + window.scrollY;

          if (left + tooltipWidth > chartRight) {
            left = chartRight - tooltipWidth - 16;
          }
          if (top + tooltipHeight > chartBottom) {
            top = chartBottom - tooltipHeight - 16;
          }

          let adjustedLeft = left;
          if (!isMobile && sidebar) {
            adjustedLeft = left - sidebar.clientWidth;
          }

          tooltip.style.left = adjustedLeft + 'px';
          tooltip.style.top = top - toolbar.clientHeight + 'px';

          tooltip.style.opacity = 1;
          tooltip.style.zIndex = 1;
        } else if (!this.isMouseOverTooltip) {
          tooltip.style.opacity = 0;
          tooltip.style.zIndex = -1;
        }
      } else if (!this.isMouseOverTooltip) {
        tooltip.style.opacity = 0;
        tooltip.style.zIndex = -1;
      }
    },

    checkChartData(data) {
      return data === GHG_PER_PRODUCT.PRODUCT_PRICE || data === GHG_PER_PRODUCT.ANNUAL_REVENUE
        ? UNIT_SETTING.CURRENCY
        : UNIT_SETTING.CO2;
    },

    // hàm chuyển đổi số có e thành string (loại bỏ e)
    convertScientificToString(value) {
      if (typeof value !== 'string') value = value.toString();

      // If not in exponential form, return as is
      if (!value.toLowerCase().includes('e')) return value;

      const [coefficient, exponent] = value.toLowerCase().split('e');
      const [intPart, decPart = ''] = coefficient.split('.');
      const exp = parseInt(exponent, 10);

      let number = intPart + decPart;
      let decimalPos = intPart.length;

      if (exp >= 0) {
        number = number.padEnd(decimalPos + exp, '0');
        return number;
      } else {
        const totalZeros = Math.abs(exp) - decimalPos;
        if (totalZeros >= 0) {
          return '0.' + '0'.repeat(totalZeros) + number;
        } else {
          const insertPos = decimalPos + exp;
          return number.slice(0, insertPos) + '.' + number.slice(insertPos);
        }
      }
    },

    customTooltip(ht) {
      const { horizontalAxis, bubbleSize, verticalAxis } = this.axisConfigData || {};
      const yDataKey = verticalAxis?.topic;
      const xDataKey = horizontalAxis?.topic;
      const sizeKey = bubbleSize?.topic;
      const yFormatUnit = this.checkChartData(yDataKey);
      const xFormatUnit = this.checkChartData(xDataKey);
      const sizeFormatUnit = this.checkChartData(sizeKey);

      let item = ht.item;
      let textName =
        this.filterType === 1
          ? this.isNewDashboarRoute
            ? `${item.name}/${item.lot_number || ''}`
            : item.name
          : item.category;
      const colorOfItem = item.color === 'defaultColor' ? this.defaultColor : this.colorMapping[item.color];
      const linkToDetailProduct = this.$router.resolve({
        name: 'DetailProduct',
        params: { id: item.id },
        query: {
          ...this.$route.query,
          type: 'detail',
        },
      }).href;

      const xAxisNum = this.convertScientificToString(item[this.xAxisKey]);
      const yAxisNum = this.convertScientificToString(item[this.yAxisKey]);
      const sizeNum = this.convertScientificToString(item.actualBubbleSizeData);

      return `
        <div class="tooltip-wrapper">
          <div class="tooltip-product-name" style="--colorOfItem: ${colorOfItem}">${textName}</div>
          <div class="tooltip-content">
            <div class="label-group">
              ${this.mappingTitle[xDataKey]}
            </div>
            <div class="product-detail">
              ${item[this.xAxisKey] === null
                ? '-'
                : this.formatNumberBySetting(xAxisNum, { formatUnit: xFormatUnit })
              } ${horizontalAxis?.unit?.name}
            </div>
            <div class="label-group">
              ${this.mappingTitle[yDataKey]}
            </div>
            <div class="product-detail">
              ${item[this.yAxisKey] === null
                ? '-'
                : this.formatNumberBySetting(yAxisNum, { formatUnit: yFormatUnit })
              } ${verticalAxis?.unit?.name}
            </div>
            <div class="label-group">
              ${this.mappingTitle[sizeKey]}
            </div>
            <div class="product-detail">
              ${item[this.bubbleSizeKey] === null
                ? '-'
                : this.formatNumberBySetting(sizeNum, { formatUnit: sizeFormatUnit })
              } ${bubbleSize?.unit?.name}
            </div>
          </div>
          ${this.filterType === 1
            ? `<a href="${linkToDetailProduct}" id="product-detail-link" class="tooltip-link">
                ${this.$t('bubble_chart.link_product_detail')}
              </a>`
            : ''
          }
          </div>
        </div>
      `;
    },
    handleConfigColor(newData) {
      const filterText = this.filterType === 1 ? 'product' : 'category';
      let newDataChart;
      const filterConfig = this.colorConfig?.[filterText] || {};
      newDataChart = newData?.map((item) => {
        const color = Object.entries(filterConfig).find(([_, items]) =>
          items.some(({ id }) => {
            return this.filterType === 1 ? id === item.id : id === item.category_id;
          }),
        )?.[0];

        return { ...item, color: color || 'defaultColor' };
      });

      this.dataChart = [...(newDataChart || [])];

      this.chart.refresh(true);
      this.prepareAxis();
    },
    redirectToDetailProduct(item) {
      const newQuery = {
        ...this.$route.query,
        type: 'detail',
      };
      this.$router.push({ name: 'DetailProduct', params: { id: item.id }, query: newQuery }).catch(() => {});
    },
  },
  watch: {
    colorConfig: {
      handler(value) {
        if (!value || !this.dataChart.length) return;
        this.handleConfigColor(this.dataChartProp);
      },
      deep: true,
    },
    axisConfigData: {
      handler(value) {
        if (!value) return;
        this.updateAxisYTitle();
        this.updateAxisXTitle();
      },
      deep: true,
    },
    dataChartProp: {
      handler(newValue) {
        this.handleConfigColor(newValue);
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.period-combobox {
  width: 100%;
  margin-bottom: 48px;
}

.container-fluid {
  .wj-control.wj-flexchart {
    border: none;
  }
}

.selection-unit-area {
  width: 356px;
  display: flex;
  flex-direction: row;
  margin: 5px auto 0 auto;
  gap: 8px;

  .select-year {
    width: 175px;
    max-width: 175px;
  }

  .select-year-disable {
    width: 240px;
    height: 30px;
    border: 1px solid rgba(42, 42, 48, 0.1);
    background-color: #f7f7f2;
    border-radius: 4px;
    color: $monoBlack;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    display: flex;
    align-items: center;
    padding: 10px;
  }

  .select-month {
    width: 90px;
    max-width: 90px;
  }
}

#customTooltip {
  position: absolute;
  opacity: 0;
  z-index: 100;
  border-radius: 4px;
  border: 1px solid #2a2a301a;
  background: $monoWhite;
  pointer-events: auto;
  min-width: 200px;
  max-width: 500px;
  box-shadow: 0px 36px 33px 0px rgba(160, 181, 186, 0.07), 0px 13.141px 12.046px 0px rgba(160, 181, 186, 0.11),
    0px 6.38px 5.848px 0px rgba(160, 181, 186, 0.13), 0px 3.127px 2.867px 0px rgba(160, 181, 186, 0.17),
    0px 1.237px 1.134px 0px rgba(160, 181, 186, 0.24);

  ::v-deep {
    .tooltip-wrapper {
      padding: 8px 12px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      color: #404d50;
      font-size: 11px;
      font-weight: 700;
      line-height: 18px;

      .tooltip-product-name {
        color: var(--colorOfItem);
        margin: 0;
        word-break: break-all;
      }

      .tooltip-content {
        display: grid;
        row-gap: 4px;
        column-gap: 8px;
        grid-template-columns: auto 1fr;

        .label-group {
          flex-shrink: 0;

        }
        .product-detail {
          font-weight: 500;
          word-break: break-all;
        }
      }
      #product-detail-link {
        font-size: 12px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 0.6px;
        text-decoration: underline;
        color: #0072a3;
      }
    }
  }
}

@include desktop {
  .period-combobox {
    width: 300px;
    margin-bottom: 48px;
  }

  .selection-unit-area {
    width: 328px;

    .select-year {
      width: 230px;
      max-width: 230px;
    }

    .select-month {
      width: 100px;
      max-width: 100px;
    }
  }

  .select-month {
    width: 100px;
    max-width: 100px;
  }
}

::v-deep .v-input--is-disabled .v-select__selections input {
  padding: 0 !important;
}
</style>
<style lang="scss">
.bubble-chart-emission {
  position: relative;

  .wj-flexchart {
    border: unset;
    position: relative;
    z-index: 1;
    max-height: 392px;

    .wj-axis-y:nth-last-child(2) {
      stroke: transparent !important;
    }

    .wj-axis-y {
      .wj-title {
        font-size: 11px;
        line-height: 18px;
        text-align: center;
        letter-spacing: 0.03em;
        color: #404d50;
        font-weight: 700;
        fill: #404d50;
        font-style: normal;
        // font-family: 'Source Han Sans JP';
      }

      .wj-gridline {
        stroke: rgba(42, 42, 48, 0.1);
      }

      .wj-label {
        fill: $goldMid !important;
        font-size: 11px;
        font-weight: 500;
        line-height: 18px;
        text-align: right;
        letter-spacing: 0.03em;

        &.unit {
          fill: $monoMid !important;
          font-size: 11px;
          line-height: 18px;
          text-align: right;
          letter-spacing: 0.03em;
        }
      }

      .wj-line {
        stroke: rgba(42, 42, 48, 0.1);
      }
    }

    .wj-axis-x {
      .wj-title {
        font-size: 11px;
        line-height: 18px;
        text-align: center;
        letter-spacing: 0.03em;
        color: #404d50;
        font-weight: 700;
        fill: #404d50;
        font-style: normal;
      }

      .wj-label {
        fill: $goldMid !important;
        font-weight: 500;
        font-size: 11px;
        line-height: 18px;
        text-align: center;
        letter-spacing: 0.03em;
      }

      .wj-line {
        stroke: rgba(42, 42, 48, 0.1);
      }
    }

    .wj-series-group {
      ellipse:not([stroke='rgba(148, 136, 16, 0.1)']) {
        stroke-width: 0;
        stroke: $monoWhite;
        stroke-width: 1;
        stroke-opacity: 0.75;
      }
    }
  }
}

.selection-unit-area {
  .v-select__selection {
    font-size: 11px !important;
  }

  .select-item {
    height: 30px;
  }

  .v-input__control {
    height: 30px !important;
    min-height: 30px !important;
  }

  .select-month {
    .v-input__control {
      min-width: 80px;
    }
  }

  .select-year {
    .v-input__control {
      width: 240px;
    }
  }
}

.v-list-item__title {
  color: $monoBlack;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
}

@include desktop {
  .select-item {
    .v-select__selection {
      overflow: inherit;
    }
  }
}

@media (max-width: 450px) {
  .selection-unit-area {
    width: 280px !important;
  }

  .select-year {
    .v-input__control {
      width: 200px !important;
      min-height: 28px !important;

      .v-select__selection {
        color: $monoBlack;
        font-size: 11px;
        font-weight: 700;
        line-height: 18px;
      }
    }

    .v-select__selections {
      height: 30px;
    }
  }

  .select-month {
    .v-input__control {
      width: 80px !important;
      min-height: 28px !important;

      .v-select__selection {
        font-size: 11px;
      }
    }

    .v-select__selections {
      height: 30px;
      font-size: 11px;
    }
  }
}
</style>
